import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
} from "@mui/material";
import Select from "react-select";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useState, useEffect } from "react";
import { useGetCategoriesQuery } from "slices/beautyApp/categorySlice";
import {
  useGetCategoryBasedPackageServicesMutation,
  useAddServicePackageMutation,
  useUpdateServicePackageMutation,
  useGetSingleServicePackageQuery,
} from "slices/beautyApp/servicePackageSlice";
import { uploadImage } from "services/imageUpload";
import { IMAGE_URL } from "network";

function AddServicePackage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: categoryResp } = useGetCategoriesQuery({});
  const [getServices] = useGetCategoryBasedPackageServicesMutation();
  const isEdit = Boolean(id);
  const { data: initialData } = useGetSingleServicePackageQuery(id, {
    skip: !isEdit,
  });

  const [add] = useAddServicePackageMutation();
  const [update] = useUpdateServicePackageMutation();
  const [servicesList, setServicesList] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [totalDiscountedPrice, setTotalPriceWithDisount] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [totalWithoutDiscountPrice, setTotalPriceWithOutDisount] = useState(0);

  // Fetch services for selected category
  const fetchServices = async (categoryId) => {
    if (!categoryId) return;
    try {
      const resp = await getServices(categoryId).unwrap();
      setServicesList(resp.data || []);
    } catch (err) {
      console.error(err);
      setServicesList([]);
    }
  };

  // Handle image upload
  const handleImageUpload = async (e, setFieldValue) => {
    const file = e.target.files[0];
    if (file) {
      const imageName = await uploadImage(file);
      setPreviewUrl(IMAGE_URL + imageName);
      setFieldValue("image", imageName);
    }
  };

  // Validation schema
  const validationSchema = Yup.object().shape({
    category: Yup.string().required("Category is required"),
    image: Yup.string().required("Image is required"),
    name: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    totalPrice: Yup.number()
      .required("Total Price is required")
      .min(0, "Must be at least 0"),
    services: Yup.array()
      .min(1, "Select at least one service type")
      .required("Service Types are required"),
  });

  const initialValues = {
    isAvailable: true,
    category: initialData?.category || "",
    name: initialData?.name || "",
    description: initialData?.description || "",
    totalPrice: initialData?.totalPrice || "",
    image: initialData?.image || "",
    services: [], // we will map backend services after servicesList loads
  };

  // Prefill preview image
  useEffect(() => {
    if (initialData?.image) {
      setPreviewUrl(IMAGE_URL + initialData.image);
    }
  }, [initialData]);

  // Prefill services list if editing
  useEffect(() => {
    if (initialData?.category) {
      fetchServices(initialData.category);
    }
  }, [initialData]);

  // Prefill selected services after servicesList is loaded - CORRECTED VERSION
  useEffect(() => {
    if (initialData?.services?.length && servicesList.length) {
      // Map the nested service objects to match with servicesList
      const preSelected = initialData.services
        .map((s) => {
          // Find the matching service from servicesList using the nested service._id
          const matchedService = servicesList.find(
            (sl) => sl._id === s.service._id
          );
          return matchedService;
        })
        .filter(Boolean);

      // console.log("Pre-selected services:", preSelected);
      setSelectedServices(preSelected);

      const total = preSelected.reduce((acc, s) => {
        const discountedPrice =
          s.servicePrice - (s.servicePrice * s.off_percentage) / 100;
        return acc + discountedPrice;
      }, 0);

      const totalWithoutDiscount = preSelected.reduce((acc, s) => {
        return acc + s.servicePrice;
      }, 0);

      setTotalPriceWithDisount(total.toFixed(2));
      setTotalPriceWithOutDisount(totalWithoutDiscount.toFixed(2));
    }
  }, [initialData, servicesList]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom>
          {isEdit ? "Edit Package" : "Add Package"}
        </Typography>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const payload = {
                  ...values,
                  services: values.services.map((s) => ({
                    service: s._id,
                    customPrice:
                      s.servicePrice -
                      (s.servicePrice * s.off_percentage) / 100,
                  })),
                };

                console.log("Submitting payload:", payload);

                if (isEdit) {
                  await update({ id, body: payload }).unwrap();
                } else {
                  await add(payload).unwrap();
                }

                navigate("/beauty-app/service-packages", {
                  state: { refresh: true },
                });
              } catch (err) {
                console.error("Submission error:", err);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              handleChange,
              isSubmitting,
            }) => (
              <Form>
                {/* Category */}
                <FormControl fullWidth margin="normal">
                  <InputLabel id="category-label">Category</InputLabel>
                  <TextField
                    select
                    name="category"
                    value={values.category}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("services", []);
                      setSelectedServices([]);
                      setFieldValue("totalPrice", "");
                      fetchServices(e.target.value);
                    }}
                    error={Boolean(touched.category && errors.category)}
                    helperText={touched.category && errors.category}
                  >
                    {categoryResp?.data
                      ?.filter((c) => c.package === "active")
                      ?.map((c) => (
                        <MenuItem key={c._id} value={c._id}>
                          {c.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </FormControl>

                {/* Availability */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isAvailable}
                      onChange={(e) =>
                        setFieldValue("isAvailable", e.target.checked)
                      }
                      name="isAvailable"
                    />
                  }
                  label={
                    values.isAvailable ? "Active Package" : "Inactive Package"
                  }
                />

                {/* Title & Description */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Title"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                  multiline
                  rows={4} // 👈 Adjust this number for desired height
                  variant="outlined"
                />

                {/* Image Upload */}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, setFieldValue)}
                />
                {previewUrl && (
                  <Box mt={2}>
                    <img
                      crossOrigin="anonymous"
                      src={previewUrl}
                      alt="Preview"
                      style={{ width: 140, borderRadius: 8 }}
                    />
                  </Box>
                )}

                {touched.image && errors.image && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {errors.image}
                  </Typography>
                )}

                {/* Service Types */}
                <Typography variant="h6" gutterBottom mt={2}>
                  Select Services
                </Typography>
                <FieldArray name="services">
                  {() => (
                    <Select
                      isMulti
                      name="services"
                      options={servicesList.map((s) => ({
                        label: s.name,
                        value: s._id,
                      }))}
                      value={selectedServices.map((s) => ({
                        label: s.name,
                        value: s._id,
                      }))}
                      onChange={(selected) => {
                        const selectedObjects =
                          selected?.map((opt) =>
                            servicesList.find((s) => s._id === opt.value)
                          ) || [];
                        console.log("selectedObjects=------------>",selectedObjects);

                        setFieldValue("services", selectedObjects);
                        setSelectedServices(selectedObjects);

                        // Calculate total price (after discount)
                        const total = selectedObjects.reduce((acc, s) => {
                          const discountedPrice =
                            s.servicePrice -
                            (s.servicePrice * s.off_percentage) / 100;
                          return acc + discountedPrice;
                        }, 0);
                        const duration = selectedObjects.reduce((acc, s) =>{
                           return acc + Number(s.duration);
                        },0)
                        // Calculate total price (before discount)
                        const totalWithoutDiscount = selectedObjects.reduce(
                          (acc, s) => {
                            return acc + s.servicePrice;
                          },
                          0
                        );
                        setTotalDuration(duration);
                        setTotalPriceWithDisount(total.toFixed(2));
                        setTotalPriceWithOutDisount(
                          totalWithoutDiscount.toFixed(2)
                        );
                      }}
                      isDisabled={!values.category}
                    />
                  )}
                </FieldArray>
                {touched.services && errors.services && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {errors.services}
                  </Typography>
                )}

                <p>Price With Discount</p>
                {totalDiscountedPrice}

                <p>Price Without discount</p>
                {totalWithoutDiscountPrice}

                <p>Total Duration</p>
                {totalDuration && totalDuration>0 ? `${totalDuration} min`: 0}

                {/* Total Price */}
                <TextField
                  fullWidth
                  margin="normal"
                  type="number"
                  label="Total Price"
                  name="totalPrice"
                  value={values.totalPrice}
                  onChange={handleChange}
                  error={Boolean(touched.totalPrice && errors.totalPrice)}
                  helperText={touched.totalPrice && errors.totalPrice}
                />

                {selectedServices?.length > 0 && (
                  <Box
                    mt={2}
                    p={2}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    {selectedServices?.map((item, i) => (
                      <Box
                        key={item._id || i}
                        sx={{
                          p: 2,
                          mb: 1,
                          border: "1px solid #ddd",
                          borderRadius: 1,
                          backgroundColor: "white",
                        }}
                      >
                        <Typography variant="body1" fontWeight="bold">
                          Service Name: {item.name}
                        </Typography>
                        <Typography variant="body2" fontWeight="text.secondary">
                          Duration: {`${item.duration} min`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Price: ₹{item.servicePrice}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Discount: {item.off_percentage}%
                        </Typography>
                        <Typography
                          variant="body2"
                          color="primary"
                          fontWeight="medium"
                        >
                          Final Price: ₹
                          {item.servicePrice -
                            (item.servicePrice * item.off_percentage) / 100}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Submit */}
                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isSubmitting}
                  >
                    {isEdit ? "Update Package" : "Create Package"}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}

export default AddServicePackage;
